import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Check, Lock, Truck, FlaskConical, RotateCcw } from "lucide-react";

import Seo from "@/components/seo/Seo";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { api } from "@/lib/api";
import { formatApiError } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { trackCart, track } from "@/lib/analytics";
import { readAttribution } from "@/lib/attribution";
import Thumb from "@/components/visual/Thumb";

const INDIAN_STATES = [
  "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa","Gujarat","Haryana",
  "Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Manipur",
  "Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana",
  "Tripura","Uttar Pradesh","Uttarakhand","West Bengal","Delhi","Jammu and Kashmir","Ladakh",
  "Andaman and Nicobar Islands","Chandigarh","Dadra and Nagar Haveli and Daman and Diu","Lakshadweep","Puducherry",
];

const RAZORPAY_SCRIPT = "https://checkout.razorpay.com/v1/checkout.js";
function loadRazorpay() {
  return new Promise((res) => {
    if (document.getElementById("rzp-js")) return res(true);
    const s = document.createElement("script");
    s.id = "rzp-js"; s.src = RAZORPAY_SCRIPT;
    s.onload = () => res(true); s.onerror = () => res(false);
    document.body.appendChild(s);
  });
}

const EMPTY_ADDRESS = {
  full_name: "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "", country: "India",
};

export default function Checkout() {
  const { user } = useAuth();
  const { items, totals, coupon, clear } = useCart();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [guestEmail, setGuestEmail] = useState("");
  const [address, setAddress] = useState(() => ({
    ...EMPTY_ADDRESS,
    full_name: user?.name || "",
  }));
  const [pinLoading, setPinLoading] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [refillDays, setRefillDays] = useState(0); // 0 = no reminder
  const [gift, setGift] = useState({ on: false, message: "" });

  // Empty cart guard
  useEffect(() => {
    if (items.length === 0) {
      toast.message("Your basket is empty.");
      nav("/products", { replace: true });
    }
  }, [items.length, nav]);

  // begin_checkout once per visit to /checkout
  useEffect(() => {
    if (items.length) trackCart("begin_checkout", items, { coupon: coupon?.code });
  }, []); // eslint-disable-line

  // Refill links carry a reorder code; apply it once the basket is ready.
  const loc = useLocation();
  const { applyCoupon } = useCart();
  useEffect(() => {
    const code = loc.state?.applyCode;
    if (code && items.length && !coupon) applyCoupon(code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Prefill name when auth loads
  useEffect(() => {
    if (user && !address.full_name) setAddress((a) => ({ ...a, full_name: user.name }));
  }, [user]); // eslint-disable-line

  const setField = (k) => (e) => setAddress((a) => ({ ...a, [k]: e.target.value }));

  const onPinBlur = async () => {
    const pin = (address.pincode || "").trim();
    if (!/^\d{6}$/.test(pin)) return;
    setPinLoading(true);
    try {
      const { data } = await api.get(`/geo/pincode/${pin}`);
      setAddress((a) => ({ ...a, city: a.city || data.city, state: a.state || data.state }));
    } catch {
      // Lookup is a convenience; the buyer can still type city and state.
    } finally { setPinLoading(false); }
  };

  const [errors, setErrors] = useState({});

  const validateStep1 = () => {
    const e = {};
    const email = user?.email || guestEmail.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = "Enter a valid email so we can send your receipt.";
    if (!address.full_name.trim()) e.full_name = "Who should we deliver to?";
    if (!/^\d{10}$/.test(address.phone.replace(/\D/g, "").slice(-10))) e.phone = "Enter a 10 digit mobile number for the courier.";
    if (!address.line1.trim()) e.line1 = "Add your house or flat number and street.";
    if (!/^\d{6}$/.test(address.pincode)) e.pincode = "Pincode is 6 digits.";
    if (!address.city.trim()) e.city = "Add your city.";
    if (!address.state) e.state = "Choose your state.";
    setErrors(e);
    if (Object.keys(e).length) {
      const first = document.querySelector(`[data-field="${Object.keys(e)[0]}"]`);
      first?.focus?.();
      return false;
    }
    return true;
  };

  const placeOrder = async () => {
    setPlacing(true);
    trackCart("add_payment_info", items, { coupon: coupon?.code, payment_type: "razorpay" });
    if (refillDays) track("refill_opt_in", { interval_days: refillDays });
    const body = {
      items: items.map((i) => ({ variant_id: i.variant_id, quantity: i.quantity })),
      address,
      guest_email: user ? null : guestEmail.trim().toLowerCase(),
      guest_phone: user ? null : address.phone,
      coupon_code: coupon?.code || null,
      refill_interval_days: refillDays || null,
      is_gift: gift.on,
      gift_message: gift.on ? gift.message.trim() || null : null,
      attribution: readAttribution(),
    };
    try {
      const { data } = await api.post("/orders/create", body);
      const ok = await loadRazorpay();
      if (!ok) { toast.error("The payment window did not load. Check your connection and try again."); setPlacing(false); return; }
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "Karijeeva",
        description: `Order ${data.order_number}`,
        order_id: data.razorpay_order_id,
        prefill: {
          name: address.full_name,
          email: user?.email || guestEmail,
          contact: address.phone,
        },
        theme: { color: "#0B0B0A" },
        handler: async (resp) => {
          try {
            await api.post("/orders/verify", {
              order_id: data.order_id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            const email = user?.email || guestEmail.trim().toLowerCase();
            clear();
            try { localStorage.removeItem("karijeeva_welcome_code_v1"); } catch {}
            nav(`/orders/${data.order_id}/confirmed?email=${encodeURIComponent(email)}`, { replace: true });
          } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail, "Payment verification failed"));
          } finally { setPlacing(false); }
        },
        modal: {
          ondismiss: () => {
            track("checkout_abandoned", { step: "payment", value: totals.total, currency: "INR" });
            toast.message("Payment not completed. Your basket is saved, pay whenever you are ready."); setPlacing(false); },
        },
      });
      rzp.on("payment.failed", (r) => {
        track("payment_failed", { reason: r?.error?.reason || r?.error?.code || "unknown", value: totals.total, currency: "INR" });
        toast.error(r?.error?.description || "Payment did not go through. No money was taken, please try again.");
        setPlacing(false);
      });
      rzp.open();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail, "Could not create order"));
      setPlacing(false);
    }
  };

  const itemCount = items.reduce((n, i) => n + i.quantity, 0);
  const summary = (
    <dl className="space-y-2 font-body text-sm">
      <Row label="Subtotal" value={`₹${totals.subtotal.toFixed(2)}`} />
      {coupon && <Row label={`Code ${coupon.code}`} value={`− ₹${totals.discount.toFixed(2)}`} className="text-brand-gold" />}
      <Row label="Delivery" value={totals.shipping === 0 ? "Free" : `₹${totals.shipping}`} />
      <div className="h-px bg-black/10 my-2" />
      <Row label={<span className="font-display text-h4 text-brand-ink">Total</span>} value={<span className="font-display text-h4 text-brand-ink" data-testid="checkout-total">₹{totals.total.toFixed(2)}</span>} />
      <p className="text-right text-xs text-ink-muted">Inclusive of GST (₹{totals.gst.toFixed(2)})</p>
    </dl>
  );

  return (
    <>
      <Seo title="Checkout" description="Secure checkout with Razorpay." />
      <section className="bg-brand-parchment min-h-screen">
        <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-10 py-8 sm:py-12">
          <div className="flex items-center justify-between">
            <h1 className="font-display text-[30px] sm:text-[38px] tracking-heading text-brand-ink">Checkout</h1>
            <p className="font-body text-xs text-ink-muted flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> Secure payment</p>
          </div>

          <ol className="mt-5 flex items-center gap-3 font-body text-[13px]" data-testid="checkout-steps">
            {["Delivery details", "Review and pay"].map((label, i) => (
              <li key={label} className="flex items-center gap-3">
                <span className={cn("h-6 w-6 rounded-full flex items-center justify-center text-[12px] font-semibold",
                  step > i + 1 ? "bg-brand-gold text-white" : step === i + 1 ? "bg-brand-ink text-white" : "bg-black/10 text-ink-muted")}>
                  {step > i + 1 ? <Check className="w-3.5 h-3.5" /> : i + 1}
                </span>
                <span className={step >= i + 1 ? "text-brand-ink" : "text-ink-muted"}>{label}</span>
                {i === 0 && <span className="w-8 h-px bg-black/15" aria-hidden="true" />}
              </li>
            ))}
          </ol>

          {/* Mobile: total always visible, items on demand */}
          <details className="lg:hidden mt-6 rounded-2xl bg-white px-5 py-4" data-testid="checkout-summary-mobile">
            <summary className="flex items-center justify-between cursor-pointer list-none font-body text-sm text-brand-ink">
              <span>{itemCount} item{itemCount === 1 ? "" : "s"} · show summary</span>
              <span className="font-semibold">₹{totals.total.toFixed(2)}</span>
            </summary>
            <div className="mt-4 space-y-3">
              {items.map((i) => <ItemLine key={i.variant_id} i={i} />)}
              <div className="pt-2">{summary}</div>
            </div>
          </details>

          <div className="mt-8 grid lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2">
              {step === 1 && (
                <form
                  noValidate
                  className="space-y-6"
                  data-testid="checkout-step-1"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!validateStep1()) return;
                    trackCart("add_shipping_info", items, { coupon: coupon?.code, shipping_tier: totals.shipping > 0 ? "standard" : "free" });
                    setStep(2);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                >
                  <fieldset className="space-y-4">
                    <legend className="font-display text-[22px] tracking-[-0.02em] text-brand-ink">Contact</legend>
                    {user ? (
                      <p className="font-body text-sm text-ink-muted">Signed in as <strong className="text-brand-ink">{user.email}</strong>. Your receipt goes here.</p>
                    ) : (
                      <>
                        <Field label="Email" error={errors.email} hint="For your receipt and delivery updates. No account needed.">
                          <Input type="email" autoComplete="email" inputMode="email" data-field="email" value={guestEmail} onChange={(e) => setGuestEmail(e.target.value)} data-testid="checkout-guest-email" className={inputCls(errors.email)} />
                        </Field>
                        <p className="font-body text-xs text-ink-muted">Ordered before? <Link to="/login?next=/checkout" className="text-brand-ink underline">Sign in</Link> to see your past orders.</p>
                      </>
                    )}
                  </fieldset>

                  <fieldset className="space-y-4">
                    <legend className="font-display text-[22px] tracking-[-0.02em] text-brand-ink">Delivery address</legend>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <Field label="Full name" error={errors.full_name}>
                        <Input autoComplete="name" data-field="full_name" value={address.full_name} onChange={setField("full_name")} data-testid="checkout-name" className={inputCls(errors.full_name)} />
                      </Field>
                      <Field label="Mobile number" error={errors.phone} hint="The courier calls this number.">
                        <Input type="tel" autoComplete="tel" inputMode="tel" data-field="phone" value={address.phone} onChange={setField("phone")} data-testid="checkout-phone" className={inputCls(errors.phone)} />
                      </Field>
                    </div>
                    <Field label="Pincode" error={errors.pincode} hint={pinLoading ? "Finding your city" : "We fill in city and state for you."}>
                      <Input autoComplete="postal-code" inputMode="numeric" maxLength={6} data-field="pincode" value={address.pincode} onChange={(e) => setAddress((a) => ({ ...a, pincode: e.target.value.replace(/\D/g, "") }))} onBlur={onPinBlur} data-testid="checkout-pincode" className={cn(inputCls(errors.pincode), "sm:max-w-[200px]")} />
                    </Field>
                    <Field label="House, flat, street" error={errors.line1}>
                      <Input autoComplete="address-line1" data-field="line1" value={address.line1} onChange={setField("line1")} data-testid="checkout-line1" className={inputCls(errors.line1)} />
                    </Field>
                    <Field label="Area, landmark (optional)">
                      <Input autoComplete="address-line2" value={address.line2} onChange={setField("line2")} data-testid="checkout-line2" className={inputCls()} />
                    </Field>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <Field label="City" error={errors.city}>
                        <Input autoComplete="address-level2" data-field="city" value={address.city} onChange={setField("city")} data-testid="checkout-city" className={inputCls(errors.city)} />
                      </Field>
                      <Field label="State" error={errors.state}>
                        <Select value={address.state} onValueChange={(v) => setAddress((a) => ({ ...a, state: v }))}>
                          <SelectTrigger data-field="state" data-testid="checkout-state" className={inputCls(errors.state)}><SelectValue placeholder="Choose" /></SelectTrigger>
                          <SelectContent>
                            {INDIAN_STATES.map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </Field>
                    </div>
                  </fieldset>

                  <Button type="submit" variant="primary" size="lg" className="w-full sm:w-auto" data-testid="checkout-step1-next">
                    Continue to review <ArrowRight />
                  </Button>
                </form>
              )}

              {step === 2 && (
                <div className="space-y-6" data-testid="checkout-step-2">
                  <div className="rounded-2xl bg-white p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="font-body text-xs font-semibold text-ink-muted">Delivering to</p>
                        <p className="mt-2 font-body text-sm text-brand-ink leading-relaxed">
                          <strong>{address.full_name}</strong> · {address.phone}<br />
                          {address.line1}{address.line2 ? `, ${address.line2}` : ""}<br />
                          {address.city}, {address.state} {address.pincode}
                        </p>
                        <p className="mt-2 font-body text-xs text-ink-muted">Receipt to {user?.email || guestEmail}</p>
                      </div>
                      <button onClick={() => setStep(1)} className="font-body text-sm text-brand-ink underline shrink-0" data-testid="checkout-edit-address">Change</button>
                    </div>
                  </div>
                  <div className="rounded-2xl bg-white p-6 space-y-3">
                    {items.map((i) => <ItemLine key={i.variant_id} i={i} />)}
                  </div>
                  <div className="rounded-2xl bg-white p-6" data-testid="checkout-refill">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input type="checkbox" className="mt-1 h-4 w-4 accent-[#2E7D4F]" checked={refillDays > 0}
                        onChange={(e) => setRefillDays(e.target.checked ? 30 : 0)} data-testid="checkout-refill-toggle" />
                      <span>
                        <span className="block font-body text-sm font-medium text-brand-ink">Remind me to refill</span>
                        <span className="block font-body text-xs text-ink-muted mt-0.5">
                          We send a WhatsApp message to {address.phone || "your number"} with a one tap reorder link. No auto debit, stop any time. <Link to="/monthly-refill" className="underline">How it works</Link>
                        </span>
                      </span>
                    </label>
                    {refillDays > 0 && (
                      <div className="mt-4 flex flex-wrap gap-2 pl-7" role="radiogroup" aria-label="Remind me every">
                        {[30, 45, 60].map((d) => (
                          <button key={d} type="button" role="radio" aria-checked={refillDays === d} onClick={() => setRefillDays(d)}
                            className={cn("h-9 px-4 rounded-control border font-body text-sm", refillDays === d ? "border-brand-gold bg-brand-gold text-white" : "border-black/15 text-brand-ink")}>
                            Every {d} days
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="rounded-2xl bg-white p-6" data-testid="checkout-gift">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input type="checkbox" className="mt-1 h-4 w-4 accent-[#2E7D4F]" checked={gift.on}
                        onChange={(e) => setGift((g) => ({ ...g, on: e.target.checked }))} data-testid="checkout-gift-toggle" />
                      <span>
                        <span className="block font-body text-sm font-medium text-brand-ink">This is a gift</span>
                        <span className="block font-body text-xs text-ink-muted mt-0.5">We add a card with your message and keep prices out of the parcel. Your invoice stays on your order page for you.</span>
                      </span>
                    </label>
                    {gift.on && (
                      <div className="mt-4 pl-7">
                        <label htmlFor="gift-message" className="block font-body text-xs text-ink-muted mb-1">Message for the card (optional)</label>
                        <textarea id="gift-message" rows={3} maxLength={250} value={gift.message}
                          onChange={(e) => setGift((g) => ({ ...g, message: e.target.value }))}
                          className="w-full rounded-xl border border-black/15 bg-white px-4 py-3 font-body text-[15px] focus:outline-none focus:ring-2 focus:ring-brand-gold"
                          placeholder="Happy housewarming, Amma. Love, Ravi" data-testid="checkout-gift-message" />
                        <p className="mt-1 text-right font-body text-[11px] text-ink-muted">{gift.message.length}/250</p>
                      </div>
                    )}
                  </div>
                  <div className="rounded-2xl bg-white p-6 lg:hidden">{summary}</div>
                  <div>
                    <Button variant="primary" size="lg" onClick={placeOrder} loading={placing} className="w-full sm:w-auto" data-testid="checkout-pay-btn">
                      <Lock className="w-4 h-4" /> Pay ₹{totals.total.toFixed(2)}
                    </Button>
                    <p className="mt-3 font-body text-xs text-ink-muted">
                      Razorpay opens next. Pay by UPI, card, netbanking or wallet. We never see or store your card details.
                    </p>
                  </div>
                  <ul className="grid sm:grid-cols-3 gap-3 font-body text-[13px] text-ink-muted">
                    <li className="flex items-start gap-2"><Truck className="w-4 h-4 text-brand-gold shrink-0" /> Dispatched in 24 to 48 hours</li>
                    <li className="flex items-start gap-2"><FlaskConical className="w-4 h-4 text-brand-gold shrink-0" /> From a lab tested batch</li>
                    <li className="flex items-start gap-2"><RotateCcw className="w-4 h-4 text-brand-gold shrink-0" /> <span><Link to="/returns-policy" className="underline">7 day returns</Link> on unopened jars</span></li>
                  </ul>
                </div>
              )}
            </div>

            <aside className="hidden lg:block lg:col-span-1 rounded-2xl bg-white p-6 h-fit lg:sticky lg:top-20" data-testid="checkout-summary">
              <h2 className="font-display text-xl text-brand-ink">Order summary</h2>
              <div className="mt-5 space-y-3 pb-5 border-b border-black/10">
                {items.map((i) => <ItemLine key={i.variant_id} i={i} />)}
              </div>
              <div className="mt-5">{summary}</div>
            </aside>
          </div>
        </div>
      </section>
    </>
  );
}

const inputCls = (err) => cn("h-12 bg-white text-[16px]", err ? "border-red-600 focus-visible:ring-red-600" : "border-black/15 focus-visible:ring-brand-gold");

function Field({ label, error, hint, children }) {
  return (
    <label className="block">
      <span className="block mb-1.5 font-body text-[13px] font-medium text-brand-ink">{label}</span>
      {children}
      {error ? (
        <span role="alert" className="block mt-1.5 font-body text-xs text-red-700">{error}</span>
      ) : hint ? (
        <span className="block mt-1.5 font-body text-xs text-ink-muted">{hint}</span>
      ) : null}
    </label>
  );
}

function ItemLine({ i }) {
  return (
    <div className="flex items-center gap-4">
      <span className="block w-14 h-14 rounded-md overflow-hidden shrink-0"><Thumb src={i.image} alt={i.product_name} /></span>
      <div className="flex-1 min-w-0">
        <p className="font-body text-sm font-medium text-brand-ink leading-snug">{i.product_name}</p>
        <p className="font-body text-xs text-ink-muted">{i.variant_size} × {i.quantity}</p>
      </div>
      <span className="font-body text-sm text-brand-ink">₹{i.unit_price * i.quantity}</span>
    </div>
  );
}

function Row({ label, value, className="" }) {
  return (
    <div className={`flex items-center justify-between ${className}`}>
      <dt>{label}</dt><dd>{value}</dd>
    </div>
  );
}
